// Shared telemetry batching queue, generic over the event shape. Extracted
// from use-softphone so the app-wide client telemetry pipeline (see
// lib/client-telemetry.ts) reuses the exact queue/flush semantics the
// softphone proved out: batch everything (never one POST per event), flush
// immediately on error severity or at the batch threshold, re-queue failed
// sends at the front, and cap the backlog with a counted drop marker.

const TELEMETRY_EVENT_FLUSH_THRESHOLD = 25;
// Flush-failure backlog cap: beyond this the oldest events are dropped and
// counted into one synthetic dropped-events marker — the forensics tripwire
// (volume spikes ARE the degrading-link signal; if it shows up, raise the cap).
const TELEMETRY_EVENT_QUEUE_MAX = 200;
// The server 413s larger batches.
const TELEMETRY_EVENTS_PER_REQUEST_MAX = 100;

export type TelemetryQueueEvent = {
  severity: 'info' | 'warning' | 'error';
};

export type GenericTelemetryEventQueue<T extends TelemetryQueueEvent> = {
  enqueue: (event: T) => void;
  flush: () => Promise<void>;
  drainForFinalFlush: () => T[];
  size: () => number;
};

// Pure queue/flush logic, exported for tests. `send` resolves true when the
// batch was delivered; false (or a throw) re-queues it for the next flush
// trigger — batching must never silently drop events (see
// TELEMETRY_EVENT_QUEUE_MAX for the only, counted, exception).
// `makeDropMarker` builds the synthetic event that carries the drop count.
export function createGenericTelemetryEventQueue<T extends TelemetryQueueEvent>(
  send: (events: T[]) => Promise<boolean>,
  makeDropMarker: (count: number) => T
): GenericTelemetryEventQueue<T> {
  const queue: T[] = [];
  let droppedCount = 0;
  let flushInFlight = false;
  let flushRequested = false;
  const isFlushRequested = () => flushRequested;

  const capQueue = () => {
    const overflow = queue.length - TELEMETRY_EVENT_QUEUE_MAX;
    if (overflow > 0) {
      queue.splice(0, overflow);
      droppedCount += overflow;
    }
  };

  const takeBatch = (): T[] => {
    const batch = queue.splice(0, droppedCount > 0 ? TELEMETRY_EVENTS_PER_REQUEST_MAX - 1 : TELEMETRY_EVENTS_PER_REQUEST_MAX);
    if (droppedCount > 0 && batch.length) {
      batch.unshift(makeDropMarker(droppedCount));
      droppedCount = 0;
    }
    return batch;
  };

  const flush = async (): Promise<void> => {
    if (flushInFlight) {
      flushRequested = true;
      return;
    }
    if (!queue.length) {
      return;
    }
    flushInFlight = true;
    flushRequested = false;
    const batch = takeBatch();
    let delivered: boolean;
    try {
      delivered = await send(batch);
    } catch {
      delivered = false;
    } finally {
      flushInFlight = false;
    }
    if (!delivered) {
      // Back at the front, ahead of anything enqueued mid-flight; the
      // interval retries rather than hot-looping against a dead uplink.
      queue.unshift(...batch);
      capQueue();
      return;
    }
    if (isFlushRequested() || queue.length >= TELEMETRY_EVENT_FLUSH_THRESHOLD) {
      await flush();
    }
  };

  return {
    enqueue: (event) => {
      queue.push(event);
      capQueue();
      if (event.severity === 'error' || queue.length >= TELEMETRY_EVENT_FLUSH_THRESHOLD) {
        // Error events are what incident forensics need promptly.
        void flush();
      }
    },
    flush,
    drainForFinalFlush: takeBatch,
    size: () => queue.length,
  };
}
